import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';
import type { Route } from '../router.ts';
import { useSession } from '../session.tsx';
import { useStreamStatus } from '../stream.tsx';
import { useNow } from '../hooks.ts';
import { fmtTime, OPERATOR_TZ, relAge, tzAbbr } from '../time.ts';
import { DevSwitcher } from './DevSwitcher.tsx';
import { PresencePill } from '../live/presence.tsx';
import { Icon } from './Icon.tsx';
import { navLayout, sectionOf, type NavLayout, type Section } from './nav.ts';

function StreamIndicator() {
  const st = useStreamStatus();
  useNow(5000);
  const text = st.state === 'open' ? 'Live' : st.state === 'connecting' ? 'Connecting' : 'Offline';
  const title =
    st.state === 'open'
      ? `Realtime stream connected${st.lastEventAt ? ` · last message ${relAge(new Date(st.lastEventAt).toISOString())}` : ''}`
      : st.state === 'down'
        ? `Realtime stream down · retrying (attempt ${st.attempts})`
        : 'Connecting to the realtime stream';
  return (
    <span className={`status ${st.state}`} title={title} role="status" aria-live="polite">
      <i aria-hidden="true" />
      {text}
    </span>
  );
}

function Clock() {
  const now = useNow(1000);
  return (
    <div className="clockbox" title={`Your time zone: ${OPERATOR_TZ}`}>
      <span className="clock">{fmtTime(now, OPERATOR_TZ)}</span>
      <span className="lbl">{tzAbbr(OPERATOR_TZ, now)} · you</span>
    </div>
  );
}

/** The overflow links, as a menu button: Enter, Space or the arrows open it; arrows, Home and End move; Esc closes. */
function MoreMenu({ nav, cur }: { nav: NavLayout; cur: Section | null }) {
  const [open, setOpen] = useState(false);
  // The item to focus once the menu renders: the first, or the last when opened with ArrowUp.
  const [start, setStart] = useState<'first' | 'last'>('first');
  const wrap = useRef<HTMLDivElement | null>(null);
  const trigger = useRef<HTMLButtonElement | null>(null);
  const menu = useRef<HTMLDivElement | null>(null);
  const id = useId();
  const items = () => [...(menu.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? [])];

  useEffect(() => {
    if (!open) return;
    const all = items();
    (start === 'last' ? all[all.length - 1] : all[0])?.focus();
    const outside = (e: MouseEvent) => {
      if (wrap.current && !wrap.current.contains(e.target as Node)) setOpen(false);
    };
    const navigated = () => setOpen(false);
    document.addEventListener('mousedown', outside);
    window.addEventListener('hashchange', navigated);
    return () => {
      document.removeEventListener('mousedown', outside);
      window.removeEventListener('hashchange', navigated);
    };
  }, [open, start]);

  const show = (from: 'first' | 'last') => {
    setStart(from);
    setOpen(true);
  };
  const close = (refocus: boolean) => {
    setOpen(false);
    if (refocus) trigger.current?.focus();
  };
  const onTriggerKey = (e: KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
      e.preventDefault();
      show('first');
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      show('last');
    }
  };
  const onMenuKey = (e: KeyboardEvent) => {
    const all = items();
    const at = all.indexOf(document.activeElement as HTMLElement);
    const go = (i: number) => all[(i + all.length) % all.length]?.focus();
    if (e.key === 'ArrowDown') go(at + 1);
    else if (e.key === 'ArrowUp') go(at - 1);
    else if (e.key === 'Home') go(0);
    else if (e.key === 'End') go(all.length - 1);
    else if (e.key === 'Escape') {
      e.stopPropagation();
      close(true);
    } else if (e.key === ' ') all[at]?.click();
    else if (e.key === 'Tab') {
      setOpen(false);
      return;
    } else return;
    e.preventDefault();
  };

  const here = nav.moreCurrent;
  return (
    <div className="nav-more" ref={wrap}>
      <button
        ref={trigger}
        type="button"
        className={here ? 'on' : ''}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? `${id}-menu` : undefined}
        onClick={() => (open ? close(false) : show('first'))}
        onKeyDown={onTriggerKey}
      >
        More
        {here && <span className="nav-more-here"> · {here.label}</span>}
        <Icon name="down" />
      </button>
      {open && (
        <div className="nav-more-menu" role="menu" id={`${id}-menu`} aria-label="More pages" ref={menu} onKeyDown={onMenuKey}>
          {nav.more.map((sec) => (
            <div key={sec.group} role={nav.more.length > 1 ? 'group' : 'none'} aria-labelledby={nav.more.length > 1 ? `${id}-${sec.group}` : undefined}>
              {nav.more.length > 1 && (
                <div className="nav-more-h" id={`${id}-${sec.group}`} role="presentation">
                  {sec.group}
                </div>
              )}
              {sec.items.map((i) => (
                <a
                  key={i.key}
                  href={i.to}
                  role="menuitem"
                  tabIndex={-1}
                  className={cur === i.key ? 'on' : ''}
                  aria-current={cur === i.key ? 'page' : undefined}
                  onClick={() => close(false)}
                >
                  <Icon name={i.icon} />
                  {i.label}
                </a>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** `phoneSlot` receives the place for the softphone's pill (telephony/CallPanel.tsx), between presence and the stream. */
export function Header({ route, phoneSlot }: { route: Route; phoneSlot?: (el: HTMLElement | null) => void }) {
  const s = useSession();
  const cur = sectionOf(route);
  const nav = navLayout((roles) => s.has(...roles), cur);
  return (
    <header className="top">
      <div className="brand">
        <img className="brand-logo" src="/brand/eyeq-logo-on-dark.png" alt="EyeQ Monitoring" />
        <img className="brand-mark" src="/brand/eyeq-mark-on-dark.png" alt="EyeQ Monitoring" />
        <span className="brand-app">SOC</span>
      </div>
      <nav className="nav" aria-label="Main">
        {nav.links.map((i) => (
          <a key={i.key} href={i.to} className={cur === i.key ? 'on' : ''} aria-current={cur === i.key ? 'page' : undefined}>
            <Icon name={i.icon} />
            {i.label}
          </a>
        ))}
        {nav.more.length > 0 && <MoreMenu nav={nav} cur={cur} />}
      </nav>
      <div className="me">
        <PresencePill />
        {phoneSlot && <span className="phone-slot" ref={phoneSlot} />}
        <StreamIndicator />
        <div className="who">
          <span className="nm" title={s.me.upn}>
            {s.me.displayName || s.me.upn}
          </span>
          <span className="roles">
            {s.me.roles.length ? s.me.roles.map((r) => <span key={r} className={`role r-${r}`}>{r}</span>) : <span className="role">no roles</span>}
          </span>
        </div>
        {s.config.authMode === 'dev' && <DevSwitcher onChange={s.reload} />}
        <Clock />
      </div>
    </header>
  );
}
